import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrl: './card.component.scss'
})
export class CardComponent implements AfterViewInit, OnChanges {
  @Input() title: string;
  @Input() currentValue: number;
  @Input() changeValue: number;
  @Input() previousValue: number;
  @Input() weeklyPledge: number;
  @Input() target: number;
  @Input() personalBest: number;
  @Input() unit: '%' | 'm' = '%';
  @Input() periodType: string;

  @ViewChild('missionChart') missionChart!: ElementRef<HTMLCanvasElement>;

  private chart?: Chart;

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['periodType'] && !changes['periodType'].firstChange) {
      this.updateChart();
    }
  }

  private getLabels(): string[] {
    switch (this.periodType) {
      case 'YTD':
        return ['Jan', 'Mar', 'May', 'Jul', 'Sep'];

      case 'QTD':
        return ['Jun','Jul', 'Aug', 'Sep', 'Today'];

      case 'MTD':
        return ['1 Sep', '8 Sep', '15 Sep', '22 Sep', 'This Wk'];

      case 'WTD':
        return ['27 Jul', '28 Jul', '29 Jul', '30 Jul', 'Today'];

      case 'PW':
        return ['19 Jul', '20 Jul', '21 Jul', '22 Jul', '23 Jul'];

      default:
        return [];
    }
  }

  private getActualValues(): number[] {
    switch (this.periodType) {
      case 'YTD':
        return [68.5, 76, 88, 90, 95.5];

      case 'QTD':
        return [68.5, 76, 88, 90, 95.5];

      case 'MTD':
        return [68.5, 76, 88, 90, 95.5];

      case 'WTD':
        return [68.5, 76, 88, 90, 95.5];

      case 'PW':
        return [68.5, 76, 88, 90, 95.5];

      default:
        return [68.5, 76, 88, 90, 95.5];
    }
  }

  private getWeeklyPledgeValues(): number[] {
    switch (this.periodType) {
      case 'YTD':
        return [75, 76, 77, 78, 82];

      case 'QTD':
        return [75, 76, 77, 78, 82];

      case 'MTD':
        return [75, 76, 77, 78, 82];

      case 'WTD':
        return [75, 76, 77, 78, 82];

      case 'PW':
        return [75, 76, 77, 78, 82];

      default:
        return [75, 76, 77, 78, 82];
    }
  }

  private createChart(): void {
    const canvas = this.missionChart?.nativeElement;

    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const labels = this.getLabels();

    const actualValues = this.getActualValues();
    const weeklyPledge = this.getWeeklyPledgeValues();

    this.chart = new Chart(ctx, {
      type: 'line',

      data: {
        labels,

        datasets: [
          {
            label: 'Actual',
            data: actualValues,

            borderColor: '#008B3A',
            backgroundColor: 'rgba(170, 238, 198, 0.15)',

            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#008B3A',
            pointBorderWidth: 2,

            tension: 0.3,
            fill: true,

            order: 1
          },
          {
            label: 'Weekly Pledge',
            data: weeklyPledge,

            borderColor: '#6C13B0',
            borderWidth: 2,
            borderDash: [5, 4],

            pointRadius: 4,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#6C13B0',
            pointBorderWidth: 2,

            tension: 0.15,
            fill: false,

            order: 0
          }
        ]
      },

      options: {
        responsive: true,
        maintainAspectRatio: false,

        layout: {
          padding: {
            top: 4,
            right: 0,
            bottom: 0,
            left: 0
          }
        },

        plugins: {
          legend: {
            display: false
          },

          tooltip: {
            callbacks: {
              label: (item) =>
                `${item.dataset.label}: ${item.formattedValue}${this.unit}`
            }
          }
        },

        scales: {
          x: {
            grid: {
              display: false
            },

            border: {
              display: false
            },

            ticks: {
              color: '#747474',
              font: {
                size: 9
              },

              maxRotation: 0,
              minRotation: 0
            }
          },

          y: {
            min: 65,
            max: 100,

            grid: {
              display: false,
            },

            border: {
              display: false
            },

            ticks: {
              display: false
            }
          }
        }
      }
    });
  }

  private updateChart(): void {
    if (!this.chart) {
      return;
    }

    const labels = this.getLabels();

    this.chart.data.labels = labels;
    this.chart.data.datasets[0].data = this.getActualValues();
    this.chart.data.datasets[1].data = this.getWeeklyPledgeValues();

    this.chart.update();
  }
}